import { useCallback, useEffect, useState } from 'react';
import { Modal, Button, Badge, Table, Alert, Spinner, Form } from 'react-bootstrap';
import { http } from '../../features/jobs/api/http';

// Two launches of the same cable, as one product — and the way back.
//
// Asked for 2026-10-26: "doar se unifica sub un job id singur". The lot adds up,
// every task and every drum keeps its id, and the only thing that disappears is
// a jobId.
//
// NOTHING IS WRITTEN UNTIL THE REPORT HAS BEEN SEEN. The endpoint answers a
// `dryRun` with the whole decision — the identity it matched on, which field the
// colour came from, what moves, which bobbins fuse, and how many rows elsewhere
// mirror the jobId it is about to retire. That report IS this modal; the confirm
// button only repeats the same call without `dryRun`.
//
// Combining is additive and therefore safe (40 000 m can satisfy everything
// 20 000 + 20 000 promised, so nothing is released and there is nothing to
// decide) — but it re-points a jobId in eight places, and a planner who cannot
// see that beforehand has no way to judge it.

const fmt = n =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString('ro-RO') : '—';

const phaseName = p => String(p ?? '').replaceAll('_', ' ');

/** The report, for either direction. */
const Blockers = ({ list, title }) =>
  list?.length ? (
    <Alert variant="danger" className="py-2 fs-9">
      <div className="fw-bold mb-1">{title}</div>
      <ul className="mb-0 ps-3">
        {list.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </Alert>
  ) : null;

const Warnings = ({ list }) =>
  list?.length ? (
    <Alert variant="warning" className="py-2 fs-9">
      <div className="fw-bold mb-1">Worth knowing</div>
      <ul className="mb-0 ps-3">
        {list.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </Alert>
  ) : null;

const CombineReport = ({ plan, preview }) => (
  <>
    <div className="mb-3">
      <div className="fs-9 text-700 mb-1">Survives — the oldest, so its job code stays on every record</div>
      <div className="fw-bold">
        {plan.primary?.productLabel}{' '}
        <span className="font-monospace fs-10 text-700">{plan.primary?.jobId}</span>
      </div>
      {plan.absorbed?.map(a => (
        <div key={a.jobId} className="mt-1">
          <Badge bg="warning" text="dark" className="me-1 fs-10">
            absorbed
          </Badge>
          {a.productLabel}{' '}
          <span className="font-monospace fs-10 text-700">{a.jobId}</span>
          {a.jobCode ? <span className="text-700 fs-10"> · {a.jobCode}</span> : null}
          <span className="text-700"> · {fmt(a.plannedLength)} m</span>
        </div>
      ))}
    </div>

    <h6 className="fs-9 fw-bold">Identity</h6>
    <Table size="sm" className="fs-10 mb-3">
      <tbody>
        <tr>
          <td className="text-700" style={{ width: 120 }}>key</td>
          <td className="font-monospace">{plan.identity?.key}</td>
        </tr>
        <tr>
          <td className="text-700">standard</td>
          <td className="font-monospace">{plan.identity?.standardId}</td>
        </tr>
        <tr>
          <td className="text-700">phases</td>
          <td>{(plan.identity?.chain || []).map(phaseName).join(' → ')}</td>
        </tr>
        {/* Where the colour was read from, per product. There is no single
            field: a job may carry it at top level, or only in the launch
            snapshot, or in its sheath — and on FY / MYF, which have no sheath,
            in the single conductor. A mismatch has to be visible, not assumed. */}
        {(plan.identity?.colours || []).map(c => (
          <tr key={c.jobId}>
            <td className="text-700">colour</td>
            <td>
              <b>{c.colour || '(unknown)'}</b>
              <span className="text-700"> — from {c.source}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>

    <h6 className="fs-9 fw-bold">Lot</h6>
    <Table size="sm" className="fs-10 mb-3">
      <tbody>
        {(plan.lot?.from || []).map(f => (
          <tr key={f.jobId}>
            <td>{f.productLabel || f.jobId}</td>
            <td className="text-end">{fmt(f.plannedLength)} m</td>
          </tr>
        ))}
        <tr className="fw-bold border-top">
          <td>Total</td>
          <td className="text-end">{fmt(plan.lot?.total)} m</td>
        </tr>
      </tbody>
    </Table>

    <h6 className="fs-9 fw-bold">
      What moves — {plan.tasks?.move?.length ?? 0} run(s) and{' '}
      {plan.tokens?.move?.length ?? 0} drum(s), keeping their ids
    </h6>
    {plan.tokens?.move?.length ? (
      <Table size="sm" className="fs-10 mb-3">
        <thead>
          <tr className="text-uppercase text-700 fs-10">
            <th>Drum</th>
            <th>Phase</th>
            <th>Colour</th>
            <th className="text-end">Made</th>
          </tr>
        </thead>
        <tbody>
          {plan.tokens.move.map(k => (
            <tr key={k.tokenId}>
              <td className="font-monospace">{k.tokenId}</td>
              <td>{phaseName(k.phase)}</td>
              <td>{k.color || '—'}</td>
              <td className="text-end">{fmt(k.producedLength)} m</td>
            </tr>
          ))}
        </tbody>
      </Table>
    ) : null}

    {/* One physical bobbin, one copy per job, each holding its share — that is
        how a merge execution is stored. Combining the products makes the whole
        bobbin theirs, so the shares add up and the two runs that made it fuse. */}
    {plan.tokens?.fuse?.length ? (
      <>
        <h6 className="fs-9 fw-bold">Shared bobbins — the shares become one drum</h6>
        <Table size="sm" className="fs-10 mb-3">
          <tbody>
            {plan.tokens.fuse.map(f => (
              <tr key={f.tokenId}>
                <td className="font-monospace">{f.tokenId}</td>
                <td>
                  {f.shares.map(s => `${fmt(s.produced)} m`).join('  +  ')} ={' '}
                  <b>{fmt(f.producedTotal)} m</b>
                  {f.keepMergeKey ? (
                    <Badge bg="info" className="ms-2 fs-10">
                      stays a merge
                    </Badge>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    ) : null}

    <h6 className="fs-9 fw-bold">What gets re-pointed</h6>
    <div className="fs-10 text-700 mb-3">
      A jobId is mirrored outside the product too, and all of it moves:
      <ul className="mb-0 ps-3 mt-1">
        <li>{plan.repoint?.queue?.length ?? 0} machine queue entr(ies)</li>
        {(plan.repoint?.records || []).map(r => (
          <li key={r.collection}>
            {r.rows} row(s) in {r.collection}
          </li>
        ))}
        {!plan.repoint?.records?.length && !plan.repoint?.queue?.length ? (
          <li>nothing — neither product is on a machine or in the records yet</li>
        ) : null}
      </ul>
    </div>

    {preview ? (
      <>
        <h6 className="fs-9 fw-bold">The product that comes out</h6>
        <Table size="sm" className="fs-10 mb-0">
          <tbody>
            <tr>
              <td className="text-700" style={{ width: 160 }}>lot</td>
              <td>
                {fmt(preview.plannedLength)} m
                <span className="text-700"> (snapshot {fmt(preview.launchTotal)} m)</span>
              </td>
            </tr>
            <tr>
              <td className="text-700">runs / drums</td>
              <td>
                {preview.tasks} / {preview.tokens}
              </td>
            </tr>
            {(preview.materialExecutionSummary || []).map((m, i) => (
              <tr key={i}>
                <td className="text-700">{i === 0 ? 'material made' : ''}</td>
                <td>
                  {m.material} {m.color || m.grade || ''} —{' '}
                  {fmt(Math.round(Number(m.executed_kg) * 10) / 10)} kg
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </>
    ) : null}
  </>
);

const UndoReport = ({ plan, preview }) => (
  <>
    <div className="mb-3 fs-9">
      Taking <b>{plan.restoring?.productLabel}</b>{' '}
      <span className="font-monospace fs-10 text-700">{plan.restoring?.jobId}</span> back
      out of <b>{plan.survivor?.productLabel}</b>, which returns to{' '}
      <b>{fmt(plan.lot?.after)} m</b> from {fmt(plan.lot?.before)} m.
    </div>
    <div className="fs-10 text-700 mb-3">
      It is restored from the archive exactly as it was — which is why the
      absorbed product was archived rather than deleted.
    </div>
    <Table size="sm" className="fs-10 mb-0">
      <tbody>
        <tr>
          <td className="text-700" style={{ width: 170 }}>goes back</td>
          <td>
            {plan.taskIds?.length ?? 0} run(s), {plan.tokenIds?.length ?? 0} drum(s)
          </td>
        </tr>
        {plan.fused?.length ? (
          <tr>
            <td className="text-700">bobbins split again</td>
            <td>
              {plan.fused
                .map(f => `${f.tokenId}: ${fmt(f.toKeep)} m + ${fmt(f.toRestore)} m`)
                .join(' · ')}
            </td>
          </tr>
        ) : null}
        {plan.queue?.length ? (
          <tr>
            <td className="text-700">queue entries</td>
            <td>{plan.queue.length} go back to the restored product</td>
          </tr>
        ) : null}
        {preview ? (
          <tr>
            <td className="text-700">after</td>
            <td>
              {fmt(preview.survivor?.plannedLength)} m ({preview.survivor?.tasks} runs) and{' '}
              {fmt(preview.restored?.plannedLength)} m ({preview.restored?.tasks} runs)
            </td>
          </tr>
        ) : null}
      </tbody>
    </Table>
  </>
);

/**
 * @param mode     'combine' | 'undo'
 * @param jobIds   the products to combine (order is irrelevant — the oldest survives)
 * @param jobId    the combined product, for 'undo'
 */
const CombineProductsModal = ({
  show,
  onHide,
  mode = 'combine',
  jobIds = [],
  jobId = null,
  onDone
}) => {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [plan, setPlan] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState('');

  const undoing = mode === 'undo';
  const url = undoing
    ? '/api/redis/jobs/uncombine-products'
    : '/api/redis/jobs/combine-products';
  const payload = undoing ? { jobId } : { jobIds };

  const run = useCallback(
    async (dryRun) => {
      const res = await http.post(url, { ...payload, reason, dryRun });
      return res?.data;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, JSON.stringify(payload), reason]
  );

  // The report is fetched on open, and it is the dry run — so the numbers the
  // planner reads are the ones the write would produce, not a second
  // implementation of them.
  useEffect(() => {
    if (!show) return;
    let alive = true;
    setLoading(true);
    setError('');
    setPlan(null);
    setPreview(null);
    setResult(null);

    run(true)
      .then(data => {
        if (!alive) return;
        setPlan(data?.plan ?? null);
        setPreview(data?.preview ?? null);
        if (!data?.success && !data?.plan) setError(data?.error || 'the preview failed');
      })
      .catch(e => {
        if (!alive) return;
        // A refusal comes back as 409 WITH the plan, which is the useful part.
        const body = e?.response?.data;
        setPlan(body?.plan ?? null);
        if (!body?.plan) setError(body?.error || e.message || 'the preview failed');
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, url, JSON.stringify(payload)]);

  const confirm = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await run(false);
      if (!data?.success) throw new Error(data?.error || 'it did not apply');
      setResult(data);
      onDone?.(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'it did not apply');
    } finally {
      setBusy(false);
    }
  };

  const ok = !!plan?.ok;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title className="fs-7">
          {undoing ? 'Undo combine' : `Combine products (${jobIds.length})`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner size="sm" /> <span className="ms-2">reading both products…</span>
          </div>
        ) : null}

        {error ? (
          <Alert variant="danger" className="py-2 fs-9">
            {error}
          </Alert>
        ) : null}

        {result ? (
          <Alert variant="success" className="py-2 fs-9">
            <div className="fw-bold mb-1">
              {undoing ? 'Taken apart.' : 'Combined.'}
            </div>
            {undoing ? (
              <>
                {result.plan?.restoring?.productLabel} is live again, and{' '}
                {result.plan?.survivor?.productLabel} is back to{' '}
                {fmt(result.plan?.lot?.after)} m.
              </>
            ) : (
              <>
                {fmt(result.plan?.lot?.total)} m under{' '}
                {result.plan?.primary?.productLabel}. The absorbed product is
                archived — its link still opens, and this can be undone.
              </>
            )}
            {result.done ? (
              <div className="mt-1 text-700">
                re-pointed: {result.done.queue ?? 0} queue entr(ies),{' '}
                {Object.entries(result.done.mongo || {})
                  .map(([c, n]) => `${n} in ${c}`)
                  .join(', ') || 'no records'}
                {result.done.archived?.length
                  ? `, archived ${result.done.archived.join(', ')}`
                  : ''}
              </div>
            ) : null}
            {result.done?.warnings?.length ? (
              <ul className="mb-0 ps-3 mt-1">
                {result.done.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            ) : null}
          </Alert>
        ) : null}

        {!result && plan ? (
          <>
            <Blockers
              list={plan.blockers}
              title={
                undoing
                  ? 'This cannot be undone yet'
                  : 'These are not the same product, or something is in the way'
              }
            />
            <Warnings list={plan.warnings} />
            {undoing ? (
              <UndoReport plan={plan} preview={preview} />
            ) : (
              <CombineReport plan={plan} preview={preview} />
            )}
          </>
        ) : null}

        {!result && ok ? (
          <Form.Group className="mt-3">
            <Form.Label className="fs-10 text-700">
              Why (goes on both products timelines)
            </Form.Label>
            <Form.Control
              size="sm"
              value={reason}
              placeholder={
                undoing ? 'e.g. combined the wrong pair' : 'e.g. two launches of one order'
              }
              onChange={e => setReason(e.target.value)}
            />
          </Form.Group>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="phoenix-secondary" onClick={onHide}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result ? (
          <Button
            variant={undoing ? 'phoenix-warning' : 'primary'}
            disabled={!ok || busy || loading}
            onClick={confirm}
          >
            {busy ? <Spinner size="sm" /> : undoing ? 'Undo it' : 'Combine them'}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
};

export default CombineProductsModal;
